import {
  Box,
  Button,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  Alert,
  AlertIcon,
  Badge,
  HStack,
  VStack,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Textarea,
  FormControl,
  FormLabel,
  useDisclosure,
  IconButton,
  Input,
  Select,
  Flex,
  Divider,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuItemOption,
  MenuGroup,
  MenuOptionGroup,
  MenuDivider,
} from "@chakra-ui/react";
import { CheckIcon, CloseIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { useHistory } from "react-router-dom";
import { useState, useEffect } from "react";
import AdminHeader from "./AdminHeader";
import {
  getAllReports,
  acceptReport,
  rejectReport,
  markReportAsRead,
  sendCustomResponse,
  getReportById,
} from "../../api/problemReports";

const AdminReportProblem = () => {
  const history = useHistory();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isViewOpen,
    onOpen: onViewOpen,
    onClose: onViewClose,
  } = useDisclosure();

  const [reports, setReports] = useState([]);
  const [allReports, setAllReports] = useState([]); // Store all reports for filtering
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  });
  const [selectedReport, setSelectedReport] = useState(null);
  const [customMessage, setCustomMessage] = useState("");
  const [sendingCustomResponse, setSendingCustomResponse] = useState(false);
  const [viewingReport, setViewingReport] = useState(null);
  const [loadingReportId, setLoadingReportId] = useState(null);
  
  // Filter states
  const [filters, setFilters] = useState({
    subject: "",
    status: "",
    dateFrom: "",
    dateTo: "",
  });

  // Header filter states
  const [nameSearchInput, setNameSearchInput] = useState("");
  const [emailSearchInput, setEmailSearchInput] = useState("");
  const [subjectSearchInput, setSubjectSearchInput] = useState("");
  const [dateSort, setDateSort] = useState("newest");
  const [statusSelections, setStatusSelections] = useState([]);
  const [subjectSelections, setSubjectSelections] = useState([]);

  const fetchReports = async (page = 1, pageSize = 10) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getAllReports(page, pageSize);
      
      if (response.success) {
        setAllReports(response.data.reports); // Store all reports
        setReports(response.data.reports);
        setPagination({
          currentPage: response.data.current_page,
          pageSize: response.data.page_size,
          totalCount: response.data.total_count,
          totalPages: response.data.total_pages,
        });
        
        // Show success toast for successful fetch
        toast({
          title: "Success",
          description:
            response.message || "Problem reports loaded successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        setError(response.message || "Failed to fetch reports");
        toast({
          title: "Error",
          description: response.message || "Failed to fetch problem reports",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || "Failed to fetch reports";
      setError(errorMessage);
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handlePageChange = (newPage) => {
    fetchReports(newPage, pagination.pageSize);
  };

  // Filter function
  const applyFilters = (reportsToFilter) => {
    return reportsToFilter.filter((report) => {
      // Subject filter
      if (filters.subject && !report.subject.toLowerCase().includes(filters.subject.toLowerCase())) {
        return false;
      }

      // Status filter
      if (filters.status && report.status !== filters.status) {
        return false;
      }

      // Date range filter
      if (filters.dateFrom || filters.dateTo) {
        const reportDate = new Date(report.created_at);
        
        if (filters.dateFrom) {
          const fromDate = new Date(filters.dateFrom);
          fromDate.setHours(0, 0, 0, 0); // Start of the day
          if (reportDate < fromDate) {
            return false;
          }
        }
        
        if (filters.dateTo) {
          const toDate = new Date(filters.dateTo);
          toDate.setHours(23, 59, 59, 999); // End of the day
          if (reportDate > toDate) {
            return false;
          }
        }
      }

      return true;
    });
  };

  // Apply filters whenever filters or allReports change
  useEffect(() => {
    if (allReports.length > 0) {
      const filteredReports = applyFilters(allReports);
      
      // Update pagination for filtered results
      const newPagination = {
        ...pagination,
        totalCount: filteredReports.length,
        totalPages: Math.ceil(filteredReports.length / pagination.pageSize),
        currentPage: 1, // Reset to first page when filters change
      };
      
      // Get the current page of filtered results
      const startIndex = (newPagination.currentPage - 1) * newPagination.pageSize;
      const endIndex = startIndex + newPagination.pageSize;
      const paginatedReports = filteredReports.slice(startIndex, endIndex);
      
      setReports(paginatedReports);
      setPagination(newPagination);
    }
  }, [filters, allReports]);

  // Apply combined header filters as well
  useEffect(() => {
    if (allReports.length > 0) {
      let filtered = [...allReports];

      // Header filters
      if (nameSearchInput) {
        const q = nameSearchInput.toLowerCase();
        filtered = filtered.filter(r => r.name && r.name.toLowerCase().includes(q));
      }
      if (emailSearchInput) {
        const q = emailSearchInput.toLowerCase();
        filtered = filtered.filter(r => r.email && r.email.toLowerCase().includes(q));
      }
      if (subjectSearchInput) {
        const q = subjectSearchInput.toLowerCase();
        filtered = filtered.filter(r => r.subject && r.subject.toLowerCase().includes(q));
      }
      if (subjectSelections.length > 0) {
        filtered = filtered.filter(r => subjectSelections.includes(r.subject));
      }
      if (statusSelections.length > 0) {
        filtered = filtered.filter(r => statusSelections.includes(r.status));
      }

      // Sort by date
      filtered.sort((a, b) => {
        const da = new Date(a.created_at);
        const db = new Date(b.created_at);
        return dateSort === "newest" ? db - da : da - db;
      });

      // Apply existing panel filters
      filtered = applyFilters(filtered);
      
      // Update pagination for filtered results
      const newPagination = {
        ...pagination,
        totalCount: filtered.length,
        totalPages: Math.ceil(filtered.length / pagination.pageSize),
        currentPage: 1, // Reset to first page when filters change
      };
      
      // Get the current page of filtered results
      const startIndex = (newPagination.currentPage - 1) * newPagination.pageSize;
      const endIndex = startIndex + newPagination.pageSize;
      const paginatedReports = filtered.slice(startIndex, endIndex);
      
      setReports(paginatedReports);
      setPagination(newPagination);
    }
  }, [nameSearchInput, emailSearchInput, subjectSearchInput, dateSort, statusSelections, subjectSelections]);

  // Handle pagination for filtered results
  const handleFilteredPageChange = (newPage) => {
    const filteredReports = applyFilters(allReports);
    const startIndex = (newPage - 1) * pagination.pageSize;
    const endIndex = startIndex + pagination.pageSize;
    const paginatedReports = filteredReports.slice(startIndex, endIndex);
    
    setReports(paginatedReports);
    setPagination(prev => ({
      ...prev,
      currentPage: newPage,
    }));
  };

  // Handle filter changes
  const handleFilterChange = (filterKey, value) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: value,
    }));
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      subject: "",
      status: "",
      dateFrom: "",
      dateTo: "",
    });
    setNameSearchInput("");
    setEmailSearchInput("");
    setSubjectSearchInput("");
    setDateSort("newest");
    setStatusSelections([]);
    setSubjectSelections([]);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const monthNames = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ];
    const month = monthNames[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month}, ${day} ${year}`;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: "yellow", text: "Pending" },
      solved: { color: "customGreen", text: "Solved" },
      rejected: { color: "red", text: "Rejected" },
      custom_response_sent: { color: "blue", text: "Custom Response" },
    };
    
    const config = statusConfig[status] || { color: "gray", text: status };
    
    return (
      <Badge colorScheme={config.color} fontSize="xs" borderRadius="xl" py={1} px={2} variant="solid">
        {config.text}
      </Badge>
    );
  };

  const handleAction = async (reportId, action) => {
    try {
      let serviceMethod = null;
      let actionName = "";
      
      switch (action) {
        case "accept":
          serviceMethod = () => acceptReport(reportId);
          actionName = "accept";
          break;
        case "reject":
          serviceMethod = () => rejectReport(reportId);
          actionName = "reject";
          break;
        case "mark-read":
          serviceMethod = () => markReportAsRead(reportId);
          actionName = "mark as read";
          break;
        default:
          return;
      }
      
      const response = await serviceMethod();
      
      if (response.success) {
        toast({
          title: "Success",
          description: response.message || `Report ${actionName} successfully`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        // Refresh the reports list
        fetchReports(pagination.currentPage, pagination.pageSize);
      } else {
        toast({
          title: "Error",
          description: response.message || `Failed to ${actionName} report`,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || `Failed to ${action} report`;
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleCustomResponse = (report) => {
    setSelectedReport(report);
    setCustomMessage("");
    onOpen();
  };

  const handleSendCustomResponse = async () => {
    if (!customMessage.trim()) {
      toast({
        title: "Error",
        description: "Please enter a custom message",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setSendingCustomResponse(true);
      const response = await sendCustomResponse(
        selectedReport._id,
        customMessage
      );
      
      if (response.success) {
        toast({
          title: "Success",
          description: response.message || "Custom response sent successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        onClose();
        setCustomMessage("");
        setSelectedReport(null);
        // Refresh the reports list
        fetchReports(pagination.currentPage, pagination.pageSize);
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to send custom response",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || "Failed to send custom response";
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSendingCustomResponse(false);
    }
  };

  const handleCloseModal = () => {
    onClose();
    setCustomMessage("");
    setSelectedReport(null);
  };

  const handleViewReport = async (reportId) => {
    try {
      setLoadingReportId(reportId);
      const response = await getReportById(reportId);
      
      if (response.success) {
        setViewingReport(response.data);
        onViewOpen();
      } else {
        toast({
          title: "Error",
          description: response.message || "Failed to fetch report details",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || "Failed to fetch report details";
      toast({
        title: "Error",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoadingReportId(null);
    }
  };

  const handleCloseViewModal = () => {
    onViewClose();
    setViewingReport(null);
  };

  return (
    <Box h="100vh" bg="white">
      <AdminHeader title="Report a Problem" />

      <Box p={4} h="90vh" overflowY="auto">
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            h="200px"
          >
            <Spinner size="xl" />
          </Box>
        ) : error ? (
          <Alert status="error">
            <AlertIcon />
            {error}
          </Alert>
        ) : (
          <VStack spacing={4} align="stretch">
            {/* <Box>
              <Text fontSize="lg" fontWeight="bold" mb={0}>
                Problem Reports ({pagination.totalCount} total)
              </Text>
            </Box> */}

            {/* Filter Controls - Commented out
            <Box
              p={4}
              bg="gray.50"
              borderRadius="md"
              border="1px"
              borderColor="gray.200"
            >
              <Flex wrap="wrap" gap={4} align="end">
                <FormControl maxW="200px">
                  <FormLabel fontSize="sm">Subject</FormLabel>
                  <Input
                    placeholder="Search by subject"
                    value={filters.subject}
                    onChange={(e) => handleFilterChange("subject", e.target.value)}
                    size="sm"
                    bg="white"
                  />
                </FormControl>

                <FormControl maxW="150px">
                  <FormLabel fontSize="sm">Status</FormLabel>
                  <Select
                    placeholder="All statuses"
                    value={filters.status}
                    onChange={(e) => handleFilterChange("status", e.target.value)}
                    size="sm"
                    bg="white"
                  >
                    <option value="pending">Pending</option>
                    <option value="solved">Solved</option>
                    <option value="rejected">Rejected</option>
                    <option value="custom_response_sent">Custom Response</option>
                  </Select>
                </FormControl>

                <FormControl maxW="150px">
                  <FormLabel fontSize="sm">From Date</FormLabel>
                  <Input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => handleFilterChange("dateFrom", e.target.value)}
                    size="sm"
                    bg="white"
                  />
                </FormControl>

                <FormControl maxW="150px">
                  <FormLabel fontSize="sm">To Date</FormLabel>
                  <Input
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => handleFilterChange("dateTo", e.target.value)}
                    size="sm"
                    bg="white"
                  />
                </FormControl>

                <Button
                  size="sm"
                  colorScheme="gray"
                  onClick={clearFilters}
                  variant="outline"
                >
                  Clear Filters
                </Button>
              </Flex>
            </Box>
            */}
            
            <Box overflowX="auto">
              <Table variant="simple" minW="800px">
                <Thead>
                  <Tr>
                    <Th>
                      <HStack spacing={2} justify="left">
                        <Text fontSize="sm" fontWeight="bold" mb={0}>From</Text>
                        <Menu closeOnSelect={false}>
                          <MenuButton as={IconButton} size="xs" variant="outline" icon={<ChevronDownIcon />} />
                          <MenuList maxH="300px" overflowY="auto">
                            <Box p={2}>
                              <Input
                                size="sm"
                                placeholder="Search by name..."
                                value={nameSearchInput}
                                onChange={(e) => setNameSearchInput(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </Box>
                            <Box p={2}>
                              <Input
                                size="sm"
                                placeholder="Search by email..."
                                value={emailSearchInput}
                                onChange={(e) => setEmailSearchInput(e.target.value)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </Box>
                          </MenuList>
                        </Menu>
                      </HStack>
                    </Th>
                    <Th>
                      <HStack spacing={2} justify="left">
                        <Text fontSize="sm" fontWeight="bold" mb={0}>Subject</Text>
                        <Menu closeOnSelect={false}>
                          <MenuButton as={IconButton} size="xs" variant="outline" icon={<ChevronDownIcon />} />
                          <MenuList>
                            <MenuGroup title="Select Subjects">
                              <MenuOptionGroup
                                type="checkbox"
                                value={subjectSelections}
                                onChange={setSubjectSelections}
                              >
                                {[
                                  { key: "Login or Account Issue", label: "Login or Account Issue" },
                                  { key: "Payment or Billing Problem", label: "Payment or Billing Problem" },
                                  { key: "Bug or Technical Error", label: "Bug or Technical Error" },
                                  { key: "Feature Not Working as Expected", label: "Feature Not Working as Expected" },
                                ].map((s) => (
                                  <MenuItemOption key={s.key} value={s.key}>
                                    {s.label}
                                  </MenuItemOption>
                                ))}
                              </MenuOptionGroup>
                            </MenuGroup>
                            <MenuDivider />
                            <MenuItem onClick={() => setSubjectSelections(["Login or Account Issue", "Payment or Billing Problem", "Bug or Technical Error", "Feature Not Working as Expected"])}>
                              Select All Subjects
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </HStack>
                    </Th>
                    <Th>
                      <HStack spacing={2} justify="left">
                        <Text fontSize="sm" fontWeight="bold" mb={0}>Date</Text>
                        <Menu>
                          <MenuButton as={IconButton} size="xs" variant="outline" icon={<ChevronDownIcon />} />
                          <MenuList>
                            <MenuItem onClick={() => setDateSort('newest')}>Newest First</MenuItem>
                            <MenuItem onClick={() => setDateSort('oldest')}>Oldest First</MenuItem>
                          </MenuList>
                        </Menu>
                      </HStack>
                    </Th>
                    <Th>View</Th>
                    <Th>
                      <HStack spacing={2} justify="left">
                        <Text fontSize="sm" fontWeight="bold" mb={0}>Status/Response</Text>
                        <Menu closeOnSelect={false}>
                          <MenuButton as={IconButton} size="xs" variant="outline" icon={<ChevronDownIcon />} />
                          <MenuList>
                            <MenuGroup title="Select Status">
                              <MenuOptionGroup
                                type="checkbox"
                                value={statusSelections}
                                onChange={setStatusSelections}
                              >
                                {[
                                  { key: "pending", label: "Open" },
                                  { key: "solved", label: "Solved" },
                                  { key: "rejected", label: "Rejected" },
                                  { key: "custom_response_sent", label: "Custom Response" },
                                ].map((s) => (
                                  <MenuItemOption key={s.key} value={s.key}>
                                    {s.label}
                                  </MenuItemOption>
                                ))}
                              </MenuOptionGroup>
                            </MenuGroup>
                            <MenuDivider />
                            <MenuItem onClick={() => setStatusSelections(["pending", "solved", "rejected", "custom_response_sent"])}>
                              Select All Status
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </HStack>
                    </Th>
                    
                  </Tr>
                </Thead>
                <Tbody>
                  {reports.length === 0 ? (
                    <Tr>
                      <Td colSpan={5} textAlign="center">
                        No problem reports found
                      </Td>
                    </Tr>
                  ) : (
                    reports.map((report) => (
                      <Tr key={report._id}>
                        <Td>
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="medium" mb={0}>
                              {report.name}
                            </Text>
                            <Text fontSize="sm" color="gray.500" mb={0}>
                              {report.email}
                            </Text>
                          </VStack>
                        </Td>
                        <Td>
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="medium" mb={0}>
                              {report.subject}
                            </Text>
                          </VStack>
                        </Td>
                        <Td>
                          <Text whiteSpace="nowrap" mb={0}>
                            {formatDate(report.created_at)}
                          </Text>
                        </Td>
                        <Td>
                          <Button
                            size="sm"
                            colorScheme="customGreen"
                            onClick={() => handleViewReport(report._id)}
                            isLoading={loadingReportId === report._id}
                          >
                            View
                          </Button>
                        </Td>
                        <Td>
                          {report.status === "pending" ? (
                            <HStack spacing={2}>
                              <IconButton
                                size="sm"
                                colorScheme="customGreen"
                                borderRadius="full"
                                icon={<CheckIcon />}
                                onClick={() => handleAction(report._id, "accept")}
                                aria-label="Accept report"
                              />
                              <IconButton
                                size="sm"
                                colorScheme="red"
                                borderRadius="full"
                                icon={<CloseIcon />}
                                onClick={() => handleAction(report._id, "reject")}
                                aria-label="Reject report"
                              />
                              <Button
                                size="sm"
                                bg="black"
                                color="white"
                                _hover="none"
                                _active="none"
                                _focus="none"
                                onClick={() => handleCustomResponse(report)}
                              >
                                Custom Response
                              </Button>
                              {!report.read && (
                                <Button
                                  size="sm"
                                  colorScheme="blue"
                                  onClick={() =>
                                    handleAction(report._id, "mark-read")
                                  }
                                >
                                  Mark Read
                                </Button>
                              )}
                            </HStack>
                          ) : (
                            <VStack align="start" spacing={1}>
                              {getStatusBadge(report.status)}
                              {!report.read && (
                                <Button
                                  size="sm"
                                  colorScheme="blue"
                                  onClick={() =>
                                    handleAction(report._id, "mark-read")
                                  }
                                >
                                  Mark Read
                                </Button>
                              )}
                            </VStack>
                          )}
                        </Td>
                        
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </Box>
            
            {pagination.totalPages > 1 && (
              <HStack justify="center" spacing={2}>
                <Button
                  size="sm"
                  onClick={() => handleFilteredPageChange(pagination.currentPage - 1)}
                  isDisabled={pagination.currentPage <= 1}
                >
                  Previous
                </Button>
                <Text>
                  Page {pagination.currentPage} of {pagination.totalPages}
                </Text>
                <Button
                  size="sm"
                  onClick={() => handleFilteredPageChange(pagination.currentPage + 1)}
                  isDisabled={pagination.currentPage >= pagination.totalPages}
                >
                  Next
                </Button>
              </HStack>
            )}
          </VStack>
        )}
      </Box>

      {/* Custom Response Modal */}
      <Modal isOpen={isOpen} onClose={handleCloseModal} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Send Custom Response</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedReport && (
              <VStack spacing={4} align="stretch">
                <FormControl isRequired>
                  <FormLabel>Custom Response Message</FormLabel>
                  <Textarea
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    placeholder="Enter your custom response message..."
                    rows={6}
                    resize="vertical"
                  />
                </FormControl>
              </VStack>
            )}
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleCloseModal}>
              Cancel
            </Button>
            <Button
              colorScheme="purple"
              onClick={handleSendCustomResponse}
              isLoading={sendingCustomResponse}
              loadingText="Sending..."
              isDisabled={!customMessage.trim()}
            >
              Send Response
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* View Report Modal */}
      <Modal
        isOpen={isViewOpen}
        onClose={handleCloseViewModal}
        size="xl"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Problem Report Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {viewingReport && (
              <VStack spacing={4} align="stretch">
                <Box>
                  <VStack spacing={3} align="stretch">
                    <HStack>
                      <Text fontSize="xl" fontWeight="semibold" color="gray.600" mb={0}>
                        Subject:
                      </Text>
                      <Text mb={0}>{viewingReport.subject}</Text>
                    </HStack>
                    <HStack>
                      <Text fontSize="sm"  fontWeight="semibold" color="gray.600" mb={0}>
                        From:
                      </Text>
                      <Text fontSize="sm" mb={0}>
                        {viewingReport.name} ({viewingReport.email})
                      </Text>
                    </HStack>
                    <HStack>
                      <Text fontSize="sm" fontWeight="semibold" color="gray.600" mb={0}>
                        Date:
                      </Text>
                      <Text fontSize="sm" mb={0}>{formatDate(viewingReport.created_at)}</Text>
                    </HStack>
                  </VStack>
                </Box>

                <Box>
                  <Box
                    p={4}
                    bg="gray.50"
                    borderRadius="md"
                    border="1px"
                    borderColor="gray.200"
                  >
                    <Text whiteSpace="pre-wrap">
                      {viewingReport.description}
                    </Text>
                  </Box>
                </Box>
                <VStack spacing={4} align="stretch">
                  {viewingReport.responded_at && (
                    <Box>
                      <Text fontWeight="semibold" color="gray.600" mb={0}>
                        Responded At:
                      </Text>
                      <Text mb={0}>
                        {formatDate(viewingReport.responded_at)}
                      </Text>
                    </Box>
                  )}
                  {viewingReport.response && (
                    <Box>
                      <Text fontWeight="semibold" color="gray.600" mb={0}>
                        Response Type:
                      </Text>
                      <Text mb={0}>{viewingReport.response}</Text>
                    </Box>
                  )}
                  {viewingReport.custom_message && (
                    <Box>
                      <Text fontWeight="semibold" color="gray.600" mb={0}>
                        Custom Message:
                      </Text>
                      <Text mb={0}>{viewingReport.custom_message}</Text>
                    </Box>
                  )}
                </VStack>
              </VStack>
            )}
          </ModalBody>

          <ModalFooter>
            {/* <Button colorScheme="blue" onClick={handleCloseViewModal}>
              Close
            </Button> */}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default AdminReportProblem;
